import BaseTypedController from "ips/ac/base/controller/BaseTypedController";
import ViewHandler from "ips/ac/base/handler/ViewHandler";
import ODataService from "ips/ac/base/service/ODataService";
import Util from "ips/ac/util/Util";
import Dialog from "sap/m/Dialog";
import Table from "sap/m/Table";
import Button from "sap/m/Button";
import SearchField from "sap/m/SearchField";
import MultiComboBox from "sap/m/MultiComboBox";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";

export type AssignToOrderType = "team" | "resource";

export interface AssignToOrderDialogData {
  orderGuid: string;
  /** binding path of the order, e.g. /Orders('...') */
  orderPath: string;
}

interface AssignToOrderConfig {
  viewName: string;
  /** navigation property of the order holding the already assigned entries */
  assignedPath: string;
  /** entity set the new mappings are created in */
  mappingEntitySet: string;
  /** property of the mapping referencing the assigned entity */
  guidProperty: string;
  searchProperties: string[];
  /** MultiComboBox ids, each id is also the filtered property */
  multiSelectFilterProperties: string[];
}

const CONFIG: Record<AssignToOrderType, AssignToOrderConfig> = {
  team: {
    viewName: "ips.ac.data.order.view.AssignTeamsToOrderDialog",
    assignedPath: "OrderTeamDetails",
    mappingEntitySet: "/OrderTeamMappings",
    guidProperty: "TeamGuid",
    searchProperties: ["Description", "Title"],
    multiSelectFilterProperties: [],
  },
  resource: {
    viewName: "ips.ac.data.order.view.AssignResourcesToOrderDialog",
    assignedPath: "OrderResourceDetails",
    mappingEntitySet: "/OrderResourceMappings",
    guidProperty: "ResourceGuid",
    searchProperties: ["Title", "Description"],
    multiSelectFilterProperties: ["Category_Value"],
  },
};

/**
 * Dialog to assign teams or resources to an order.
 * Shared by AssignTeamsToOrderDialog.view.xml and AssignResourcesToOrderDialog.view.xml.
 * @namespace ips.ac.data.order.controller
 */
export default class AssignToOrderDialogController extends BaseTypedController {
  private dialog: Dialog;
  private table: Table;
  private addButton: Button;
  private config: AssignToOrderConfig;
  private data: AssignToOrderDialogData;
  private excludedGuidFilter: Filter;
  private onAssigned: () => void;

  /**
   * Loads the dialog view for the given type and opens it.
   */
  public static async open(type: AssignToOrderType, data: AssignToOrderDialogData, onAssigned: () => void): Promise<void> {
    const view = await ViewHandler.getView(CONFIG[type].viewName, Util.getComponent());
    await (view.getController() as AssignToOrderDialogController).openDialog(type, data, onAssigned);
  }

  public onInit(): void {
    super.onInit();
    this.dialog = this.byId("assignDialog") as Dialog;
    this.table = this.byId("assignTable") as Table;
    this.addButton = this.byId("addButton") as Button;
  }

  public async openDialog(type: AssignToOrderType, data: AssignToOrderDialogData, onAssigned: () => void): Promise<void> {
    this.config = CONFIG[type];
    this.data = data;
    this.onAssigned = onAssigned;
    this.table.removeSelections(true);
    this.onSelectionChange();
    this.dialog.open();
    this.dialog.setBusy(true);
    try {
      this.excludedGuidFilter = await this.loadExcludedGuidFilter();
      this.onFilter();
    } finally {
      this.dialog.setBusy(false);
    }
  }

  /**
   * Excludes all entries that are already assigned to the order.
   */
  private async loadExcludedGuidFilter(): Promise<Filter | undefined> {
    const binding = Util.getODataModel().bindList(this.data.orderPath + "/" + this.config.assignedPath);
    try {
      const contexts = await binding.requestContexts(0, Infinity);
      const filters = contexts.map((context) => new Filter("Guid", FilterOperator.NE, context.getProperty(this.config.guidProperty)));
      return filters.length > 0 ? new Filter(filters, true) : undefined;
    } finally {
      binding.destroy();
    }
  }

  public onFilter(): void {
    const filters: Filter[] = [
      new Filter("CreatedBy_ClientGuid", FilterOperator.EQ, Util.getAppModel().getProperty("/Client/Guid")),
    ];

    this.config.multiSelectFilterProperties.forEach((property) => {
      const keys = (this.byId(property) as MultiComboBox).getSelectedKeys();
      if (keys.length > 0) {
        filters.push(new Filter(keys.map((key) => new Filter(property, FilterOperator.EQ, key)), false));
      }
    });

    const searchValue = (this.byId("searchField") as SearchField).getValue();
    if (searchValue) {
      filters.push(new Filter(this.config.searchProperties.map((property) => new Filter({
        path: property,
        operator: FilterOperator.Contains,
        value1: searchValue,
        caseSensitive: false,
      })), false));
    }

    if (this.excludedGuidFilter) {
      filters.push(this.excludedGuidFilter);
    }

    const binding = this.table.getBinding("items") as ODataListBinding;
    binding.filter(new Filter(filters, true));
    if (binding.isSuspended()) {
      binding.resume();
    }
  }

  public onSelectionChange(): void {
    this.addButton.setEnabled(this.table.getSelectedItems().length > 0);
  }

  public async onAdd(): Promise<void> {
    const guids = this.table.getSelectedItems().map((item) => item.getBindingContext().getProperty("Guid") as string);
    const binding = Util.getODataModel().bindList(this.config.mappingEntitySet);
    guids.forEach((guid) => {
      binding.create({
        OrderGuid: this.data.orderGuid,
        [this.config.guidProperty]: guid,
      }, true);
    });

    this.dialog.setBusy(true);
    try {
      await ODataService.submitBatch();
      this.closeDialog();
      this.onAssigned();
    } catch (err) {
      console.error(err);
      await binding.resetChanges();
    } finally {
      binding.destroy();
      this.dialog.setBusy(false);
    }
  }

  public onCancel(): void {
    this.closeDialog();
  }

  private closeDialog(): void {
    this.dialog.close();
  }
}
